import { useRef, useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR, { mutate as swrMutate } from 'swr';
import { isAxiosError } from 'axios';
import {
  Eye,
  EyeOff,
  ExternalLink,
  ChevronDown,
  Loader2,
  MoreHorizontal,
  RefreshCw,
  Router as RouterIcon,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Popover } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import {
  deleteZoneRouter,
  getZoneRouter,
  getZoneRouterProbeCache,
  probeZoneRouter,
  type RouterProbeCacheResponse,
  type RouterProbeOk,
  type RouterProbeResponse,
  type RouterRecord,
} from '@/api/zoneRouter';
import { extractApiError } from '@/utils/apiError';
import { AdminTunnelButton } from './AdminTunnelButton';
import {
  RouterProbeContent,
  RouterProbeErrorRow,
} from './RouterAutodiscoverButton';
import RouterEditDialog from './RouterEditDialog';
import { RouterWifiStats } from './RouterWifiStats';
import PortForwardsTable from './PortForwardsTable';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Tooltip } from '@/components/ui/tooltip';

interface Props {
  zoneId: number | string;
  /** Fired after the router autodiscovery panel imports new cameras
   *  so the parent (EquipmentSettings) can refresh the Cameras list. */
  onCamerasImported?: () => void;
}

/**
 * Equipment-tab Router card.
 *
 * 2026-06-02 redesign (Group B) — see
 * `docs/superpowers/specs/2026-06-02-discovery-redesign.md`:
 *
 *   Header     icon + Роутер + subcaption("keenetic") · big-caption(SKU)
 *              (right) updated-time + refresh icon
 *   Sub-card   Подключение (URL / Логин / Пароль) — sub-card title is
 *              the sub-card divider, no top-level section label.
 *   Sub-card   Сводка — small. Model+firmware on the left, WG state pill
 *              on the right.
 *   Collapse   Возможные камеры (default closed when probe is fresh)
 *   Collapse   Проброс портов (default closed when probe is fresh)
 *   Notes      plain prose if non-empty
 *   Footer     admin-tunnel + "..." overflow → Изменить + Удалить
 */
export default function RouterPanel({ zoneId, onCamerasImported }: Props) {
  const { t } = useT();
  const swrKey = `zone-router:${zoneId}`;
  const { data, error, isLoading } = useSWR<RouterRecord, { response?: { status?: number } }>(
    swrKey,
    () => getZoneRouter(zoneId).then(r => r.data),
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    },
  );

  const exists = !!data;
  const notFound = error?.response?.status === 404;
  const otherError = error && !notFound;

  const reload = () => swrMutate(swrKey);

  if (!isLoading && !exists && (notFound || !error)) return null;
  if (isLoading && !data && !error) return null;

  return (
    <Card variant="section">
      <CardContent className="space-y-5">
        {isLoading && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
          </div>
        )}

        {otherError && !data && <ServerStatusNotice variant="card" error={error} />}

        {!isLoading && exists && (
          <RouterCardBody
            zoneId={zoneId}
            record={data}
            onChanged={reload}
            onCamerasImported={onCamerasImported}
          />
        )}
      </CardContent>
    </Card>
  );
}

function RouterCardBody({
  zoneId,
  record,
  onChanged,
  onCamerasImported,
}: {
  zoneId: number | string;
  record: RouterRecord;
  onChanged: () => void;
  onCamerasImported?: () => void;
}) {
  const { t } = useT();
  const { user } = useCurrentUser();
  const isSuperuser = user?.is_superuser === true;
  const [showPw, setShowPw] = useState(false);

  // Probe state — both sourced (cache + live).
  const probeCacheKey = `zone-router-probe-cache:${zoneId}`;
  const { data: cacheResp, mutate: mutateCache } = useSWR<RouterProbeCacheResponse>(
    probeCacheKey,
    () => getZoneRouterProbeCache(zoneId).then(r => r.data),
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    },
  );

  const [livePayload, setLivePayload] = useState<RouterProbeResponse | null>(null);
  const [liveFetchedAt, setLiveFetchedAt] = useState<string | null>(null);
  const [probing, setProbing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const cachedPayload = cacheResp?.cached ? cacheResp.payload : null;
  const cachedFetchedAt = cacheResp?.cached ? cacheResp.fetched_at : null;

  const probeOk: RouterProbeOk | null =
    livePayload && livePayload.ok ? livePayload : (cachedPayload || null);
  const probeErr =
    livePayload && livePayload.ok === false ? livePayload : null;
  const fetchedAt = liveFetchedAt || cachedFetchedAt || null;

  const [editOpen, setEditOpen] = useState(false);
  const [forceAdvancedOpen, setForceAdvancedOpen] = useState(false);

  const [armed, setArmed] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const runProbe = async (): Promise<RouterProbeResponse | null> => {
    setProbing(true);
    setRefreshError(null);
    try {
      const r = await probeZoneRouter(zoneId);
      const now = new Date().toISOString();
      setLivePayload(r.data);
      if (r.data.ok) {
        setLiveFetchedAt(now);
        await mutateCache();
      }
      return r.data;
    } catch (err) {
      setRefreshError(
        extractApiError(
          isAxiosError(err) ? err : undefined,
          err instanceof Error ? err.message : 'unknown error',
        ),
      );
      return null;
    } finally {
      setProbing(false);
    }
  };

  const onSavedAndProbe = async () => {
    setForceAdvancedOpen(false);
    onChanged();
    const result = await runProbe();
    if (!result || result.ok !== true) {
      setForceAdvancedOpen(true);
      setEditOpen(true);
    }
  };

  const onDelete = async () => {
    if (!armed) {
      setArmed(true);
      window.setTimeout(() => setArmed(false), 5000);
      return;
    }
    setDeleting(true);
    try {
      await deleteZoneRouter(zoneId);
      onChanged();
    } finally {
      setDeleting(false);
    }
  };

  // Resolve model display: vendor sub-caption + the full SKU as the
  // big caption, per the redesign spec.
  const vendorCaption = (record.model_name || '').toLowerCase()
    ? record.model_name
    : '';
  const sku = probeOk?.model || record.model || '';
  const firmware = probeOk?.firmware_version || record.firmware_version || '';

  // Resolve LAN-side IP. Priority:
  //   1. Persisted gateway_ip on the record (operator-edited).
  //   2. Most common /24 prefix from the probe's LAN hosts + ".1"
  //      (a Keenetic typically lives at <prefix>.1 — heuristic but
  //      stable enough for "what's my gateway" display until the BE
  //      learns to backfill the column from the probe).
  const derivedLanIp = (() => {
    if (record.gateway_ip) return record.gateway_ip;
    const counts = new Map<string, number>();
    for (const h of probeOk?.lan_hosts || []) {
      const m = (h.ip || '').match(/^(\d+\.\d+\.\d+)\.\d+$/);
      if (!m) continue;
      counts.set(m[1], (counts.get(m[1]) || 0) + 1);
    }
    if (counts.size === 0) return '';
    const [prefix] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
    return `${prefix}.1`;
  })();
  const wanIp = record.wan_ip || '';

  return (
    <div className="space-y-5 text-sm">
      {/* ----- Header ----- */}
      <CardHeader
        vendorCaption={vendorCaption}
        sku={sku}
        fetchedAt={fetchedAt}
        probing={probing}
        onRefresh={() => { void runProbe(); }}
      />

      {refreshError && (
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {t('equipment.router.refreshError')}{' '}
          <span className="opacity-80">— {refreshError}</span>
        </div>
      )}

      {/* ----- Two sub-cards side-by-side on desktop ----- */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
        <SubCard className="sm:col-span-3" testId="router-connection-subcard">
          {/* Connection sub-card. No section header — sub-card frame is
              the label (the redesign explicitly drops the «Подключение»
              header before the URL/login/password fields). */}
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            <FieldRow
              label={t('equipment.router.adminUrl')}
              value={record.admin_url}
              link={record.admin_url || undefined}
              mono
            />
            <FieldRow
              label={t('equipment.router.username')}
              value={record.username || ''}
              mono
            />
            <div className="min-w-0 space-y-1 sm:col-span-2">
              <FieldLabel>{t('equipment.router.password')}</FieldLabel>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm tabular-nums">
                  {record.password ? (showPw ? record.password : '••••••••') : t('equipment.router.dash')}
                </span>
                {record.password && (
                  <button
                    type="button"
                    onClick={() => setShowPw(v => !v)}
                    className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    aria-label={showPw ? t('equipment.router.hide') : t('equipment.router.show')}
                  >
                    {showPw ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                  </button>
                )}
              </div>
            </div>
          </div>
        </SubCard>

        <SubCard className="sm:col-span-2" testId="router-status-subcard">
          {/* Overview sub-card — small. Lead is the LAN/WAN ip pair;
              WG state pill anchors the right column. */}
          {!probeOk && !probing ? (
            <div className="text-xs text-muted-foreground" data-testid="router-status-block">
              {t('equipment.router.fetchedNever')}
            </div>
          ) : (
            <div className="space-y-2.5" data-testid="router-status-block">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <FieldLabel>{t('equipment.router.gatewayIp')}</FieldLabel>
                  <div className="font-mono text-sm tabular-nums">
                    {derivedLanIp || t('equipment.router.dash')}
                  </div>
                  {wanIp && (
                    <div className="font-mono text-[11px] tabular-nums text-muted-foreground">
                      WAN {wanIp}
                    </div>
                  )}
                </div>
                {probeOk && <WgPill probe={probeOk} />}
              </div>
              {firmware && (
                <div className="text-[11px] tabular-nums text-muted-foreground">
                  {t('equipment.router.firmwareInline').replace('{{version}}', firmware)}
                </div>
              )}
            </div>
          )}
        </SubCard>
      </div>

      {probeErr && (
        <RouterProbeErrorRow error={probeErr.error} detail={probeErr.detail} />
      )}

      {/* ----- Suggested cameras — collapsible ----- */}
      {probeOk && (
        <CollapsibleSection
          testId="router-section-suggested"
          title={t('equipment.router.sectionDevices.collapsed')}
          countSuffix={
            (probeOk.likely_cameras_count || 0) + (probeOk.likely_nvrs_count || 0)
          }
          defaultOpen={false}
        >
          <RouterProbeContent
            probe={probeOk}
            zoneId={zoneId}
            onCamerasImported={() => {
              onCamerasImported?.();
              // Re-probe the router so the just-imported camera flips to
              // "уже добавлено" in the discovered-peer list immediately —
              // the imported_as_camera_id annotation is recomputed by the
              // probe's _annotate_lan_hosts, so without this the badge only
              // appears on the next manual refresh.
              void runProbe();
            }}
          />
        </CollapsibleSection>
      )}

      {/* ----- Port-forwards — collapsible ----- */}
      {probeOk && (
        <CollapsibleSection
          testId="router-section-forwards"
          title={t('equipment.router.sectionForwards.collapsed')}
          countSuffix={probeOk.existing_forwards_count}
          defaultOpen={false}
        >
          <PortForwardsTable
            zoneId={zoneId}
            forwards={probeOk.existing_forwards || []}
            lanHosts={probeOk.lan_hosts || []}
            defaultInterface={
              probeOk.wg_state === 'configured' && probeOk.wg_uplink_ip
                ? 'Wireguard0'
                : undefined
            }
            onChanged={() => { void runProbe(); }}
          />
        </CollapsibleSection>
      )}

      {record.notes && record.notes.trim() && (
        <section className="space-y-2.5 border-t border-border pt-4">
          <SectionLabel>{t('equipment.router.notes')}</SectionLabel>
          <div className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{record.notes}</div>
        </section>
      )}

      <RouterWifiStats zoneId={zoneId} />

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        {isSuperuser && (
          <AdminTunnelButton
            kind="routers"
            id={record.zone_id}
            capability={record.admin_tunnel_capability}
          />
        )}
        <OverflowMenu
          armed={armed}
          deleting={deleting}
          onEdit={() => { setForceAdvancedOpen(false); setEditOpen(true); }}
          onDelete={() => { void onDelete(); }}
        />
      </div>

      <RouterEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        zoneId={zoneId}
        initial={record}
        forceAdvancedOpen={forceAdvancedOpen}
        onSaved={() => { void onSavedAndProbe(); }}
      />
    </div>
  );
}

/**
 * Card header — Роутер icon + title + the vendor/SKU pair.
 *
 * Subcaption is the tiny operator-typed vendor name; big caption is
 * the full probed SKU (e.g. "Netcraze Hopper 4G+ (NC-2312)"). When the
 * SKU isn't known yet we fall back to a quiet "Модель неизвестна"
 * placeholder so the typographic rhythm doesn't collapse.
 */
function CardHeader({
  vendorCaption,
  sku,
  fetchedAt,
  probing,
  onRefresh,
}: {
  vendorCaption: string;
  sku: string;
  fetchedAt: string | null;
  probing: boolean;
  onRefresh: () => void;
}) {
  const { t } = useT();
  const timestamp = fetchedAt ? formatFetchedAt(fetchedAt, t) : null;
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-2.5">
        <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-primary-muted text-primary-muted-foreground">
          <RouterIcon className="size-4" />
        </span>
        <div className="min-w-0 space-y-0.5">
          <h2 className="truncate text-base font-semibold leading-tight">
            {t('equipment.router.title')}
          </h2>
          <div className="min-w-0 leading-tight">
            {vendorCaption && (
              <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/80">
                {vendorCaption}
              </div>
            )}
            <Tooltip content={sku || undefined}><div
              className={cn(
                'truncate text-sm font-medium',
                sku ? 'text-foreground/90' : 'italic text-muted-foreground/70',
              )}
            >
              {sku || t('equipment.router.modelUnknown')}
            </div></Tooltip>
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {probing ? (
          <span
            data-testid="router-fetched-at"
            className="text-xs tabular-nums text-muted-foreground"
          >
            {t('equipment.router.refreshing')}
          </span>
        ) : (
          timestamp && (
            <span
              data-testid="router-fetched-at"
              className="text-xs tabular-nums text-muted-foreground"
            >
              {timestamp}
            </span>
          )
        )}
        <Tooltip content={t('equipment.router.refreshTooltip')}><button
          type="button"
          onClick={onRefresh}
          disabled={probing}
          className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-all hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
          aria-label={t('equipment.router.refresh')}
          data-testid="router-refresh"
        >
          {probing ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <RefreshCw className="size-4" />
          )}
        </button></Tooltip>
      </div>
    </div>
  );
}

/**
 * Sub-card used by the Подключение / Сводка split. The frame replaces
 * the section-label header for these two blocks — the visual hierarchy
 * is: card → sub-card → field rows, no third-level title between them.
 */
function SubCard({
  className,
  testId,
  children,
}: {
  className?: string;
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        // On <md the parent Card is frameless, so a full sub-frame would
        // re-introduce a box on the edge-to-edge section. Use a top/bottom
        // divider + zero horizontal padding instead; restore the full
        // rounded frame + inset padding on md+.
        'border-y border-border bg-card/40 px-0 py-3 transition-colors',
        'md:rounded-xl md:border md:px-3.5',
        className,
      )}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

/**
 * Compact WG state pill anchored to the top-right of the overview
 * sub-card. Three states: configured (green dot + uplink IP), absent,
 * wrong_scope (orange dot + label).
 */
function WgPill({ probe }: { probe: RouterProbeOk }) {
  const { t } = useT();
  const ok = probe.wg_state === 'configured' && !!probe.wg_uplink_ip;
  const wrong = probe.wg_state === 'wrong_scope';
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em]',
        ok
          ? 'bg-[color:var(--color-success)]/12 text-[color:var(--color-success)]'
          : 'bg-[color:var(--color-warning)]/12 text-[color:var(--color-warning)]',
      )}
    >
      <span
        className={cn(
          'size-1.5 rounded-full',
          ok ? 'bg-[color:var(--color-success)]' : 'bg-[color:var(--color-warning)]',
        )}
        aria-hidden
      />
      WG
      <span className="font-normal tracking-normal">
        {ok
          ? ` · ${probe.wg_uplink_ip}`
          : wrong
            ? ` · ${t('equipment.router.statusWgWrongScope')}`
            : ` · ${t('equipment.router.statusWgAbsent')}`}
      </span>
    </span>
  );
}

/**
 * Collapsible section using a CSS-only `<details>` element. Provides
 * a title bar with the optional count chip + a chevron that rotates
 * on open. Kept independent of the SubCard frame so the inner content
 * carries its own visual rhythm.
 */
function CollapsibleSection({
  title,
  countSuffix,
  defaultOpen = false,
  testId,
  children,
}: {
  title: string;
  countSuffix?: number;
  defaultOpen?: boolean;
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <details
      className="group rounded-xl border border-border bg-card/40"
      open={defaultOpen}
      data-testid={testId}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3.5 py-2.5 transition-colors hover:bg-muted/30">
        <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground/80">
          {title}
          {typeof countSuffix === 'number' && countSuffix > 0 && (
            <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
              {countSuffix}
            </span>
          )}
        </span>
        <ChevronDown className="size-4 text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <div className="border-t border-border px-3.5 py-3.5">{children}</div>
    </details>
  );
}

function formatFetchedAt(
  iso: string,
  t: (k: string) => string,
): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = Date.now();
  const ageMs = now - date.getTime();
  if (ageMs < 60_000) return t('equipment.router.fetchedJustNow');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return t('equipment.router.fetchedAt').replace('{{time}}', `${hh}:${mm}`);
}

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
      {children}
    </div>
  );
}

export function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
      {children}
    </div>
  );
}

function FieldRow({
  label,
  value,
  mono = false,
  link,
}: {
  label: string;
  value: string;
  mono?: boolean;
  link?: string;
}) {
  const { t } = useT();
  const display = value || t('equipment.router.dash');
  return (
    <div className="min-w-0 space-y-1">
      <FieldLabel>{label}</FieldLabel>
      {link ? (
        <Tooltip content={link}><a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'inline-flex max-w-full items-start gap-1 text-sm text-primary hover:underline',
            mono && 'font-mono',
          )}
        >
          {/* min-w-0 + break-all so a long URL (whose `/` and `.` aren't
              break opportunities for break-words) shrinks and wraps inside
              the card instead of overflowing onto the next field. */}
          <span className="min-w-0 break-all">{display}</span>
          <ExternalLink className="mt-0.5 size-3 shrink-0" />
        </a></Tooltip>
      ) : (
        <div className={cn('break-words text-sm', mono && 'font-mono')}>{display}</div>
      )}
    </div>
  );
}

function OverflowMenu({
  armed,
  deleting,
  onEdit,
  onDelete,
}: {
  armed: boolean;
  deleting: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useT();
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('equipment.router.actionsLabel')}
        data-testid="router-overflow"
      >
        <MoreHorizontal className="size-4" />
      </button>
      <Popover open={open} onOpenChange={setOpen} triggerRef={ref} side="top" align="end">
        <div className="flex min-w-[10rem] flex-col py-1" role="menu">
          <MenuItem
            onClick={() => { setOpen(false); onEdit(); }}
            testId="router-edit"
          >
            {t('common.edit')}
          </MenuItem>
          <MenuItem
            destructive
            disabled={deleting}
            onClick={() => {
              onDelete();
              if (armed) setOpen(false);
            }}
            testId="router-delete"
          >
            {deleting ? <Loader2 className="size-3.5 animate-spin" /> : null}
            {armed ? t('equipment.router.deleteConfirm') : t('equipment.router.delete')}
          </MenuItem>
        </div>
      </Popover>
    </>
  );
}

function MenuItem({
  onClick,
  destructive = false,
  disabled = false,
  testId,
  children,
}: {
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      role="menuitem"
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors',
        destructive
          ? 'text-destructive hover:bg-destructive/10'
          : 'hover:bg-muted hover:text-foreground',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      {children}
    </button>
  );
}
